import { Link, useSearch } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";

import { ApiError, resetPassword } from "../api/client";
import { Button, Card, ErrorNote, Field, Input } from "../ui/primitives";

/** Mirrors the register form, so both places a password is chosen agree. */
const MIN_PASSWORD = 8;

/**
 * fastapi-users reports reset failures as bare codes. Shown raw, a person who
 * clicked an old link reads "RESET_PASSWORD_BAD_TOKEN" and has no idea the
 * fix is one more email.
 */
function explain(error: unknown): unknown {
  if (!(error instanceof ApiError) || error.status !== 400) return error;
  if (error.message.includes("RESET_PASSWORD_BAD_TOKEN")) {
    return new Error("This link has expired or has already been used. Ask for a new one below.");
  }
  if (error.message.includes("RESET_PASSWORD_INVALID_PASSWORD")) {
    return new Error("That password is not allowed. Try a longer one.");
  }
  return error;
}

export function ResetPasswordPage() {
  const { token } = useSearch({ from: "/reset-password" });

  const [password, setPassword] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!token || tooShort) return;

    setError(null);
    setBusy(true);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (caught) {
      setError(explain(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main id="main" className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center px-6">
      <h1 className="mb-1 text-xl font-semibold">Choose a new password</h1>
      <p className="mb-6 text-sm text-muted">
        This signs you out everywhere else.
      </p>

      <Card>
        {done ? (
          <div role="status" className="space-y-4 text-sm">
            <p>Your password has been changed.</p>
            <Link to="/login" className="text-brand underline">
              Sign in with your new password
            </Link>
          </div>
        ) : !token ? (
          // Someone typed the address, or a mail client cut the link short.
          // A form that can only fail is worse than saying so.
          <p role="alert" className="text-sm">
            This link is incomplete. Open the link from the email again, or ask for a
            new one.
          </p>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {error != null && <ErrorNote error={error} />}

            <Field
              label="New password"
              id="password"
              hint={`At least ${MIN_PASSWORD} characters.`}
              error={tooShort ? `Use at least ${MIN_PASSWORD} characters.` : null}
            >
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>

            <Button
              type="submit"
              variant="primary"
              className="w-full"
              disabled={busy || tooShort}
            >
              {busy ? "Saving…" : "Set new password"}
            </Button>
          </form>
        )}
      </Card>

      {!done && (
        <p className="mt-4 text-center text-sm text-muted">
          <Link to="/forgot-password" className="text-brand underline">
            Send me a new link
          </Link>
        </p>
      )}
    </main>
  );
}
